import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-pro";
import { SyntheticEvent } from "react";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import type {
  BaseTableRow,
  TableColumnDef,
  TableProps,
  TableState
} from "../../types/components/table.types";
import { getHeaderName } from "../../types/components/table.types";
import { highlightKeyword } from "../../utilities";
import { ProfilePicture } from "../ProfilePicture";
import { Table } from "./Table";

/** Manager reference */
interface ManagerInfo {
  fullName?: string;
  jobTitle?: string;
}

/** Job/Position row data structure */
interface JobTableRow extends BaseTableRow {
  rowId?: string;
  name?: string;
  fullName?: string;
  jobHierarchy?: string[];
  isJob?: boolean;
  deleted?: boolean;
  manager?: ManagerInfo;
  numberOfTeamMembers?: number;
  numberOfPositions?: number;
}

/** Props specific to JobTable */
interface JobTableProps<T extends JobTableRow = JobTableRow> extends TableProps<T> {
  onJobAction?: (event: SyntheticEvent, row: T) => void;
  onPositionAction?: (event: SyntheticEvent, row: T) => void;
  focusPosition?: T;
}

export class JobTable<T extends JobTableRow = JobTableRow> extends Table<
  T,
  JobTableProps<T>,
  TableState
> {
  static columns = {
    name: {
      field: "name",
      label: "tableColumns.name"
    },
    profilePicFullName: {
      field: "profilePicFullName",
      label: "tableColumns.name"
    },
    reportsTo: {
      field: "reportsTo",
      label: "tableColumns.reportsTo",
      visibleFromBreakpoint: "md"
    },
    reports: {
      field: "reports",
      label: "tableColumns.reports",
      visibleFromBreakpoint: "md"
    },
    positions: {
      field: "positions",
      label: "tableColumns.positions",
      visibleFromBreakpoint: "md"
    },
    actions: {
      field: "actions",
      label: "tableColumns.actions"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [
      { type: JobTable.columns.profilePicFullName },
      { type: JobTable.columns.reportsTo },
      { type: JobTable.columns.reports },
      { type: JobTable.columns.positions },
      { type: JobTable.columns.actions }
    ],
    noRowsMessage: "",
    rowHasAction: null,
    tree: true,
    disableSelection: true
  };

  deletedIds: string[];

  constructor(props: JobTableProps<T>) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [
            {
              field: "profilePicFullName",
              sort: "asc"
            }
          ]
    };
    this.deletedIds = [];
  }

  componentDidUpdate(prevProps: Readonly<JobTableProps<T>>, prevState: Readonly<TableState>): void {
    super.componentDidUpdate(prevProps, prevState);
    if (prevProps.focusPosition !== this.props.focusPosition) {
      const position = this.props.focusPosition;
      const api = this.apiRef.current;
      if (position) {
        const jobRowId = this.getRowId(position);
        try {
          api.setRowChildrenExpansion?.(jobRowId, true);
        } catch (error) {
          // Silently handle expansion errors
        }
      }
    }
    if (this.apiRef.current) {
      this.apiRef.current.subscribeEvent?.("rowExpansionChange", () => {
        setTimeout(() => {
          if (this.deletedIds.length) {
            for (const id of this.deletedIds) {
              const element = document.querySelector(`[data-id=${id}]`) as HTMLElement | null;
              if (element && element.style) element.style.display = "none";
            }
          }
        }, 0);
      });
    }

    if (prevProps.keyword !== this.props.keyword) {
      this.initColumns();
      this.forceUpdate();
    }
  }

  getRowId(e: T): string {
    return this.tableId + "_" + e.rowId;
  }

  getTreeDataPath(row: T): string[] {
    return row.jobHierarchy || [];
  }

  onRowClick(params: GridRowParams<T>, e: SyntheticEvent): void {
    const { onJobSelect } = this.props;
    if (params.row.isJob && onJobSelect) onJobSelect(e, params.row);
  }

  initColumns(): void {
    const { columns, sortable, onJobAction, onPositionAction, keyword } = this.props;

    this.columns = [];

    if (!columns) return;

    columns.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === JobTable.columns.name)
          this.columns.push({
            field: "name",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            flex: 1,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => (row.deleted ? "WW" : row.isJob ? row.name : row.fullName)
          });
      }

      if (c.type === JobTable.columns.profilePicFullName)
        this.columns.push({
          field: "profilePicFullName",
          sortable: sortable,
          headerName: getHeaderName(c, this.props.translate),
          flex: 1,
          filterable: false,
          disableReorder: true,
          valueGetter: (_value, row) => (row.deleted ? "WW" : row.isJob ? row.name : row.fullName),
          renderCell: (params) =>
            params.row.deleted ? null : params.row.isJob ? (
              highlightKeyword(params.row.name || "", keyword)
            ) : (
              <div className='klayo_employeetable_pic klayo_employeetable_picname'>
                <ProfilePicture
                  user={params.row}
                  size='sm'
                  onClick={(e: SyntheticEvent) => this.onUserClick(e, params.row)}
                />
                <div>
                  <div className='klayo_employeetable_name'>{params.row.fullName}</div>
                </div>
              </div>
            )
        });

      if (this.hasColumn(c)) {
        if (c.type === JobTable.columns.reportsTo)
          this.columns.push({
            field: "reportsTo",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            width: 250,
            visibleFromBreakpoint: c.visibleFromBreakpoint || c.type.visibleFromBreakpoint,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) =>
              row.deleted ? null : row.manager ? row.manager.fullName : "",
            renderCell: (params) =>
              params.row.deleted ? null : params.row.manager ? (
                <div className='klayo_employeetable_pic klayo_employeetable_picname'>
                  <div>
                    <div className='klayo_employeetable_name'>{params.row.manager.fullName}</div>
                    <div className='klayo_employeetable_job'>{params.row.manager.jobTitle}</div>
                  </div>
                </div>
              ) : (
                ""
              )
          });
      }

      if (this.hasColumn(c)) {
        if (c.type === JobTable.columns.reports)
          this.columns.push({
            field: "reports",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: c.visibleFromBreakpoint || c.type.visibleFromBreakpoint,
            filterable: false,
            disableReorder: true,
            width: 100,
            type: "number",
            valueGetter: (_value, row) => (row.deleted ? null : row ? row.numberOfTeamMembers : "")
          });
      }

      if (this.hasColumn(c)) {
        if (c.type === JobTable.columns.positions)
          this.columns.push({
            field: "positions",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint:
              c.visibleFromBreakpoint ||
              (c.type as { visibleFromBreakpoint?: string }).visibleFromBreakpoint,
            filterable: false,
            disableReorder: true,
            width: 100,
            type: "number",
            valueGetter: (_value, row) =>
              row.deleted ? null : row.isJob ? row.numberOfPositions : ""
          });
      }

      if (c.type === JobTable.columns.actions)
        this.columns.push({
          field: "actions",
          type: "actions",
          filterable: false,
          disableReorder: true,
          align: "right",
          valueGetter: (_value, row) => {
            return row.deleted ? null : row;
          },
          getActions: (params) =>
            params.row.deleted
              ? []
              : [
                  <GridActionsCellItem
                    icon={<MoreVertIcon />}
                    label='Actions'
                    onClick={(e: any) =>
                      params.row.isJob
                        ? onJobAction?.(e, params.row)
                        : onPositionAction?.(e, params.row)
                    }
                    key={Math.random().toString(36).substring(7)}
                  />
                ]
        });
    });
  }
}
